import React from "react";
import ConditionRow from "../components/ConditionRow";
import { ColumnSourceEnum, PropertyBrowserTreeNode } from "api-client";
import { PROCESSING_RULE_OPERATORS } from "features/MappingTools/DynamicMappingEditor/const";
import Button from "components/Button";
import { useTranslation } from "react-i18next";
import { IconPlus } from "@tabler/icons-react";
import clsx from "clsx";
import { rulesSetStore } from "../RulesSetStore";
import { observer } from "mobx-react-lite";

export const EMPTY_CONDITION = {
  value: "",
  valueProperty: undefined,
  operator: PROCESSING_RULE_OPERATORS[0].id,
  property: undefined,
  hasError: false,
};

const RuleConditions = () => {
  const { t } = useTranslation();
  const conditions = rulesSetStore.currentRule?.conditions || [];

  const handleCopyRule = async (index: number) => {
    const newConditions = [...conditions, { ...conditions[index] }];
    rulesSetStore.setRuleConditions(newConditions);
    await rulesSetStore.updateRule(rulesSetStore.currentRule);
  };

  const handleRemoveCondition = (index: number) => {
    rulesSetStore.setRuleConditions(conditions.filter((_, i) => i !== index));
    rulesSetStore.updateRule(rulesSetStore.currentRule);
  };

  const handleSelectProperty = async (
    index: number,
    property: PropertyBrowserTreeNode | undefined
  ) => {
    const newConditions = [...conditions];
    newConditions[index].property = property;
    rulesSetStore.setRuleConditions(newConditions);
    await rulesSetStore.checkForUpdateRule(index);
  };

  const handleSelectOperator = async (
    index: number,
    operator: string | undefined | number,
    updateState?: boolean
  ) => {
    const newConditions = [...conditions];
    const currentCondition = newConditions[index];
    currentCondition.operator = String(operator);
    const validCondition = Boolean(
      currentCondition.property?.id &&
        (String(currentCondition.value).length ||
          currentCondition.valueProperty?.id ||
          currentCondition.operator?.includes("EMPTY"))
    );
    if (currentCondition.operator.includes("EMPTY")) {
      currentCondition.value = "";
      currentCondition.valueProperty = undefined;
    }
    rulesSetStore.setRuleConditions(newConditions);
    if (validCondition && updateState) {
      await rulesSetStore.checkForUpdateRule(index);
    }
  };

  const handleSelectValue = async (
    index: number,
    value: PropertyBrowserTreeNode | string | undefined
  ) => {
    const newConditions = [...conditions];
    const currentCondition = newConditions[index];
    if (typeof value === "string") {
      currentCondition.value = value;
      currentCondition.value_source = ColumnSourceEnum.Literal;
    } else {
      currentCondition.valueProperty = value;
      currentCondition.value = "";
      currentCondition.value_source = value?.column_source as ColumnSourceEnum;
    }
    if (currentCondition.property && value) {
      currentCondition.hasError = false;
    }
    rulesSetStore.setRuleConditions(newConditions);
    if (currentCondition.property) {
      await rulesSetStore.checkForUpdateRule(index);
    }
  };

  const handleRemoveValue = async (index: number) => {
    const newConditions = [...conditions];
    const currentCondition = newConditions[index];
    currentCondition.value = "";
    currentCondition.valueProperty = undefined;
    currentCondition.value_source = undefined;
    rulesSetStore.setRuleConditions(newConditions);
    if (currentCondition.property) {
      await rulesSetStore.checkForUpdateRule(index);
    }
  };

  const addNewCondition = () => {
    rulesSetStore.setRuleConditions([...conditions, { ...EMPTY_CONDITION }]);
  };

  return (
    <div className="p-6 flex flex-col justify-items-center items-center flex-grow">
      {conditions.map((item, index) => (
        <ConditionRow
          key={index}
          properties={rulesSetStore.properties}
          index={index}
          row={item}
          onCopyRow={() => handleCopyRule(index)}
          onRemoveRow={
            conditions.length > 1
              ? () => handleRemoveCondition(index)
              : undefined
          }
          onSelectProperty={(property) => handleSelectProperty(index, property)}
          onSelectOperator={(operator, updateState) =>
            handleSelectOperator(index, operator, updateState)
          }
          onSelectValue={(value) => handleSelectValue(index, value)}
          onRemoveProperty={() => handleSelectProperty(index, undefined)}
          onRemoveValue={() => handleRemoveValue(index)}
        />
      ))}
      <Button
        type="link"
        className={clsx(
          "text-indigo-700 text-base font-medium mt-1 hover:!bg-indigo-50 hover:!text-indigo-700 !w-fit"
        )}
        width="fit-content"
        leadingIcon={<IconPlus size={20} stroke={2.5} />}
        onClick={addNewCondition}
      >
        {t("rulesEditor.addFurtherCondition")}
      </Button>
    </div>
  );
};

export default observer(RuleConditions);
